import { create } from 'zustand';
import { apiClient } from '../api-client';
import type {
  StageLayout,
  StageObject,
  ShootingPosition,
  Wall,
  Annotation,
  StageMetadata,
  CanvasConfig,
} from '@/stage/types';
import { createDefaultLayout } from '@/stage/defaults';
import { normalizeLayoutRotations } from '@/stage/migration';
import axios from 'axios';

export interface StageState {
  layout: StageLayout | null;
  matchId: string | null;
  stageId: string | null;
  serverUpdatedAt: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Server operations
  loadFromServer: (matchId: string, stageId: string) => Promise<void>;
  saveToServer: () => Promise<void>;
  loadFromJson: (json: string) => void;
  exportToJson: () => string;
  setLayout: (layout: StageLayout) => void;
  initDefault: (name?: string) => void;

  // Object CRUD
  addObject: (object: StageObject) => void;
  updateObject: (id: string, updates: Partial<StageObject>) => void;
  removeObject: (id: string) => void;

  // Position CRUD
  addPosition: (position: ShootingPosition) => void;
  updatePosition: (id: string, updates: Partial<ShootingPosition>) => void;
  removePosition: (id: string) => void;

  // Wall CRUD
  addWall: (wall: Wall) => void;
  updateWall: (id: string, updates: Partial<Wall>) => void;
  removeWall: (id: string) => void;

  // Annotation CRUD
  addAnnotation: (annotation: Annotation) => void;
  updateAnnotation: (id: string, updates: Partial<Annotation>) => void;
  removeAnnotation: (id: string) => void;

  // Metadata and canvas
  updateMetadata: (metadata: Partial<StageMetadata>) => void;
  updateCanvas: (canvas: Partial<CanvasConfig>) => void;
}

export const useStageStore = create<StageState>((set, get) => ({
  layout: null,
  matchId: null,
  stageId: null,
  serverUpdatedAt: null,
  isLoading: false,
  isSaving: false,
  error: null,

  loadFromServer: async (matchId: string, stageId: string) => {
    set({ isLoading: true, error: null, matchId, stageId });
    try {
      const response = await apiClient.get(`/stages/${stageId}/diagram`);
      const data = response?.data || response;

      if (data.diagram_data) {
        set({
          layout: normalizeLayoutRotations(data.diagram_data as StageLayout),
          serverUpdatedAt: data.updated_at,
          isLoading: false,
        });
      } else {
        // No existing diagram — create default
        const defaultLayout = createDefaultLayout('Untitled Stage');
        set({
          layout: defaultLayout,
          serverUpdatedAt: data.updated_at,
          isLoading: false,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load stage';
      console.error('Load error:', error);
      // Fall back to default layout
      set({
        layout: createDefaultLayout('Untitled Stage'),
        error: message,
        isLoading: false,
      });
    }
  },

  saveToServer: async () => {
    const state = get();
    if (!state.layout || !state.stageId) {
      throw new Error('No layout or stage to save');
    }

    set({ isSaving: true, error: null });
    try {
      const response = await apiClient.put(`/stages/${state.stageId}/diagram`, {
        diagram_data: state.layout,
        expected_updated_at: state.serverUpdatedAt || new Date().toISOString(),
      });

      const data = response?.data || response;
      set({
        serverUpdatedAt: data.updated_at || new Date().toISOString(),
        isSaving: false,
      });
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.status === 409
        ? 'Stage was modified by another user. Reload to see their changes.'
        : error instanceof Error ? error.message : 'Failed to save';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  loadFromJson: (json: string) => {
    try {
      const layout = normalizeLayoutRotations(JSON.parse(json) as StageLayout);
      set({ layout, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON';
      set({ error: message });
    }
  },

  exportToJson: () => {
    const state = get();
    if (!state.layout) throw new Error('No layout to export');
    return JSON.stringify(state.layout, null, 2);
  },

  setLayout: (layout: StageLayout) => {
    set({ layout: normalizeLayoutRotations(layout), error: null });
  },

  initDefault: (name?: string) => {
    set({ layout: createDefaultLayout(name || 'Untitled Stage'), error: null });
  },

  // Object CRUD
  addObject: (object: StageObject) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          objects: [...state.layout.objects, object],
        },
      };
    });
  },

  updateObject: (id: string, updates: Partial<StageObject>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          objects: state.layout.objects.map((obj) =>
            obj.id === id ? { ...obj, ...updates } : obj
          ),
        },
      };
    });
  },

  removeObject: (id: string) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          objects: state.layout.objects.filter((obj) => obj.id !== id),
        },
      };
    });
  },

  // Position CRUD
  addPosition: (position: ShootingPosition) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          shooting_positions: [...state.layout.shooting_positions, position],
        },
      };
    });
  },

  updatePosition: (id: string, updates: Partial<ShootingPosition>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          shooting_positions: state.layout.shooting_positions.map((pos) =>
            pos.id === id ? { ...pos, ...updates } : pos
          ),
        },
      };
    });
  },

  removePosition: (id: string) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          shooting_positions: state.layout.shooting_positions.filter((pos) => pos.id !== id),
        },
      };
    });
  },

  // Wall CRUD
  addWall: (wall: Wall) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          walls: [...state.layout.walls, wall],
        },
      };
    });
  },

  updateWall: (id: string, updates: Partial<Wall>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          walls: state.layout.walls.map((wall) =>
            wall.id === id ? { ...wall, ...updates } : wall
          ),
        },
      };
    });
  },

  removeWall: (id: string) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          walls: state.layout.walls.filter((wall) => wall.id !== id),
        },
      };
    });
  },

  // Annotation CRUD
  addAnnotation: (annotation: Annotation) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          annotations: [...state.layout.annotations, annotation],
        },
      };
    });
  },

  updateAnnotation: (id: string, updates: Partial<Annotation>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          annotations: state.layout.annotations.map((ann) =>
            ann.id === id ? { ...ann, ...updates } : ann
          ),
        },
      };
    });
  },

  removeAnnotation: (id: string) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          annotations: state.layout.annotations.filter((ann) => ann.id !== id),
        },
      };
    });
  },

  // Metadata and canvas
  updateMetadata: (metadata: Partial<StageMetadata>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          metadata: { ...state.layout.metadata, ...metadata },
        },
      };
    });
  },

  updateCanvas: (canvas: Partial<CanvasConfig>) => {
    set((state) => {
      if (!state.layout) return {};
      return {
        layout: {
          ...state.layout,
          canvas: { ...state.layout.canvas, ...canvas },
        },
      };
    });
  },
}));
